import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  ButtonBase,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { Fragment, useEffect } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import {
  createClosureCustomField,
  Field,
  FieldTypeEnum,
  updateClosureCustomField,
} from 'wisestep-api';
import { ControlledTextInput, Label, WsColors } from 'wisestep-base';

import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { FormProps } from '../../clientsSettings/components/FormProps';
import { FieldIconMap, FieldNameMap, fieldTypes } from '../../invoicing/helper/FieldHelper';

const mapFieldFormToDTO = (field: FieldForm) => ({
  ...field,
  ...(field.options && { options: field.options.map(({ value }) => value) }),
});

const StyledRadio = styled(ButtonBase)<{ checked: boolean }>(({ checked }) => ({
  width: '100%',
  height: '6rem',
  alignItems: 'flex-start',
  padding: '1.25rem 1rem',
  borderRadius: '0.25rem',
  ...(checked && {
    color: WsColors.TheBlues500,
    border: '0.0625rem solid',
  }),
}));

const fieldFormDefaultValues: FieldForm = {
  name: '',
  label: '',
  description: '',
  required: false,
  visible: true,
  type: FieldTypeEnum.TextBox,
  options: [],
};

interface FieldForm extends Omit<Field, 'options'> {
  options: { value: string }[];
}

export const CustomFieldFormClosure = ({
  defaultValues = fieldFormDefaultValues,
  onSave,
  onClose,
}: FormProps<FieldForm>) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const form = useForm({
    defaultValues,
  });
  const {
    fields: options,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: 'options',
  });
  const selectedType = form.watch('type');
  const label = form.watch('label');
  const edit = !!defaultValues.id;

  useEffect(() => {
    const { isTouched } = form.getFieldState('name');
    if (!isTouched && !edit) {
      form.setValue(
        'name',
        label
          .trim()
          .toLowerCase()
          .replace(/[\s]+/g, '_')
          .replace(/[^\w-]+/g, '')
      );
    }
  }, [edit, form, label]);

  const onSubmit = async (formData) => {
    try {
      formData.id
        ? await updateClosureCustomField(mapFieldFormToDTO(formData))
        : await createClosureCustomField(mapFieldFormToDTO(formData));
      setNotification(success(`Custom FieldClousre saved successfully!`));
      onSave();
    } catch (error: any) {
      if (error.response.status === 400) {
        setNotification(warning(`Oops, already exists!!`));
      } else setNotification(failed('Something went wrong !!'));
    }
  };

  return (
    <>
      <DialogTitle>Closures FieldClousre</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Stack direction="row" spacing={4} px={2} alignItems="flex-start">
            <Grid container alignItems="center" sx={{ flex: 1 }}>
              {fieldTypes.map((type) => {
                const Icon = FieldIconMap[type];
                let checked = false;
                if (selectedType) {
                  checked = selectedType === type;
                }
                return (
                  <Grid key={type} item sm={4}>
                    <StyledRadio
                      onClick={() => form.setValue('type', FieldTypeEnum[type])}
                      checked={checked}
                    >
                      <Stack alignItems="center" justifyContent="flex-start" spacing={1}>
                        <Icon />
                        <span>{FieldNameMap[type]}</span>
                      </Stack>
                    </StyledRadio>
                  </Grid>
                );
              })}
            </Grid>
            <Stack spacing={2} px={2} justifyContent="flex-start" sx={{ flex: 1 }}>
              <Box>
                <ControlledTextInput
                  name="label"
                  type="text"
                  fullWidth
                  labelText="FieldClousre Name"
                  variant="outlined"
                />
              </Box>
              <Box>
                <ControlledTextInput
                  name="description"
                  type="text"
                  fullWidth
                  labelText="FieldClousre Description"
                  variant="outlined"
                />
              </Box>
              <Box>
                <ControlledTextInput
                  name="name"
                  type="text"
                  disabled={edit}
                  fullWidth
                  labelText="Code"
                  variant="outlined"
                  required
                />
              </Box>
              {(selectedType === 'Dropdown' || selectedType === 'Checkbox') && (
                <Stack>
                  <Label>Options</Label>
                  <Grid container rowSpacing={1} alignItems="center">
                    {options.map((option, index) => {
                      return (
                        <Fragment key={option.id}>
                          <Grid item sm={11}>
                            <ControlledTextInput
                              fullWidth
                              size="small"
                              inputProps={{}}
                              name={`options.${index}.value`}
                              placeholder={`Option ${index + 1}`}
                            />
                          </Grid>
                          <Grid item sm={1}>
                            <IconButton onClick={() => remove(index)}>
                              <CloseIcon />
                            </IconButton>
                          </Grid>
                        </Fragment>
                      );
                    })}
                    <Grid item sm={12}>
                      <Button
                        sx={{ px: 0 }}
                        onClick={() => append({})}
                        variant="text"
                        startIcon={<AddIcon />}
                      >
                        Add Option
                      </Button>
                    </Grid>
                    {options.length > 1 && selectedType === 'Dropdown' && (
                      <Grid item sm={12}>
                        <WsCheckBox name="multiple" label="Allow multiple selection" />
                      </Grid>
                    )}
                  </Grid>
                </Stack>
              )}
              <Box mx={-2}>
                <WsCheckBox name="required" label="Mandatory" />
              </Box>
              <Box mx={-2}>
                <WsCheckBox name="contract" label="Contract Placement" />
              </Box>
              <Box mx={-2}>
                <WsCheckBox name="permanent" label="Permanent Placement" />
              </Box>
            </Stack>
          </Stack>
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit">Save</Button>
          </DialogActions>
        </form>
      </FormProvider>
    </>
  );
};
